
import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Order, OrderStatus } from '../types';
import { X, Search, Filter, ShoppingBag, Truck, CheckCircle, Clock, FileText, User } from 'lucide-react';

interface OrderManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const OrderManager: React.FC<OrderManagerProps> = ({ isOpen, onClose }) => {
  const { orders, fetchOrders, updateOrderStatus, config } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
        setLoading(true);
        fetchOrders().finally(() => setLoading(false));
    }
  }, [isOpen]);

  const filteredOrders = orders.filter(order => {
      const matchesSearch = 
          order.extOrderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer.lastName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      return matchesSearch && matchesStatus;
  });

  const selectedOrder = orders.find(o => o.id === selectedOrderId);

  const handleStatusChange = async (status: OrderStatus) => {
      if (selectedOrderId) {
          await updateOrderStatus(selectedOrderId, status);
      }
  };

  const getStatusBadge = (status: OrderStatus) => {
      switch(status) {
          case 'new': return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs uppercase font-bold">Nowe</span>;
          case 'paid': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs uppercase font-bold">Opłacone</span>;
          case 'shipped': return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs uppercase font-bold">Wysłane</span>;
          case 'completed': return <span className="bg-gray-800 text-white px-2 py-1 rounded text-xs uppercase font-bold">Zakończone</span>;
          case 'cancelled': return <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-xs uppercase font-bold">Anulowane</span>;
          default: return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs uppercase font-bold">{status}</span>;
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
      {/* Backdrop Click Handler */}
      <div className="absolute inset-0" onClick={onClose}></div>
      
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl overflow-hidden flex h-[80vh] relative z-10">
        
        {/* SIDEBAR: LIST */}
        <div className="w-1/3 border-r bg-gray-50 flex flex-col">
            <div className="p-4 border-b bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                        <ShoppingBag size={20} className="text-blue-600"/> Zamówienia
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-500" title="Zamknij">
                        <X size={20}/>
                    </button>
                </div>
                <div className="space-y-2">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-2.5 text-gray-400"/>
                        <input 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border rounded bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Szukaj (ID, Email, Nazwisko)..."
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                        <button onClick={() => setStatusFilter('all')} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap border ${statusFilter === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600'}`}>Wszystkie</button>
                        <button onClick={() => setStatusFilter('paid')} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap border ${statusFilter === 'paid' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600'}`}>Opłacone</button>
                        <button onClick={() => setStatusFilter('shipped')} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap border ${statusFilter === 'shipped' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600'}`}>Wysłane</button>
                    </div>
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Ładowanie...</div>
                ) : filteredOrders.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Brak zamówień.</div>
                ) : (
                    filteredOrders.map(order => (
                        <div 
                            key={order.id}
                            onClick={() => setSelectedOrderId(order.id)}
                            className={`p-4 border-b cursor-pointer transition-colors hover:bg-gray-100 ${selectedOrderId === order.id ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className="font-bold text-sm text-gray-900">{order.extOrderId}</span>
                                {getStatusBadge(order.status)}
                            </div>
                            <p className="text-xs text-gray-500 mb-1">{order.customer.firstName} {order.customer.lastName}</p>
                            <div className="flex justify-between items-center mt-2">
                                <span className="text-xs text-gray-400">{order.date.split(' ')[0]}</span>
                                <span className="font-bold text-sm">{order.totalAmount.toFixed(2)} PLN</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>

        {/* MAIN: DETAILS */}
        <div className="flex-1 flex flex-col bg-white">
            {selectedOrder ? (
                <>
                    <div className="p-6 border-b flex justify-between items-center">
                        <div>
                            <h3 className="font-bold text-xl text-gray-900">Zamówienie #{selectedOrder.extOrderId}</h3>
                            <p className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                <Clock size={12}/> {selectedOrder.date}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-500 uppercase">Status:</span>
                                <select 
                                    value={selectedOrder.status}
                                    onChange={(e) => handleStatusChange(e.target.value as OrderStatus)}
                                    className="border rounded px-2 py-1 text-sm font-bold bg-gray-50"
                                >
                                    <option value="new">Nowe</option>
                                    <option value="paid">Opłacone</option>
                                    <option value="processing">W Realizacji</option>
                                    <option value="shipped">Wysłane</option>
                                    <option value="completed">Zakończone</option>
                                    <option value="cancelled">Anulowane</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-8 space-y-8">
                        {/* CUSTOMER & SHIPPING */}
                        <div className="grid grid-cols-2 gap-8">
                            <div className="bg-gray-50 p-4 rounded border">
                                <h4 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2"><User size={16}/> Dane Klienta</h4>
                                <div className="space-y-1 text-sm text-gray-600">
                                    <p className="font-bold text-black">{selectedOrder.customer.firstName} {selectedOrder.customer.lastName}</p>
                                    <p>{selectedOrder.customer.email}</p>
                                    <p>{selectedOrder.customer.phone}</p>
                                    <p className="mt-2 text-xs text-gray-400 uppercase">Adres wysyłki:</p>
                                    {selectedOrder.delivery.method === 'pickup' ? (
                                        <p className="font-bold">Odbiór osobisty</p>
                                    ) : selectedOrder.delivery.method === 'paczkomat' ? (
                                        <div>
                                            <p className="font-bold">Paczkomat: {selectedOrder.delivery.pointName}</p>
                                            <p className="text-xs">{selectedOrder.delivery.pointAddress}</p>
                                        </div>
                                    ) : (
                                        <p>{selectedOrder.customer.street}, {selectedOrder.customer.zipCode} {selectedOrder.customer.city}</p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded border">
                                <h4 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2"><FileText size={16}/> Dane do Dokumentu</h4>
                                <div className="space-y-1 text-sm text-gray-600">
                                    {selectedOrder.documentType === 'receipt' ? (
                                        <p>Paragon</p>
                                    ) : (
                                        <>
                                            <p className="font-bold uppercase mb-1">{selectedOrder.documentType === 'invoice' ? 'Faktura VAT' : 'Paragon z NIP'}</p>
                                            {selectedOrder.billing && (
                                                <>
                                                    <p>NIP: <span className="font-mono bg-white px-1 rounded">{selectedOrder.billing.nip}</span></p>
                                                    {selectedOrder.billing.companyName && <p>{selectedOrder.billing.companyName}</p>}
                                                    {selectedOrder.billing.address && <p>{selectedOrder.billing.address}</p>}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ITEMS */}
                        <div>
                            <h4 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2"><ShoppingBag size={16}/> Produkty</h4>
                            <div className="border rounded overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 text-gray-500 font-medium border-b">
                                        <tr>
                                            <td className="px-4 py-3">Produkt</td>
                                            <td className="px-4 py-3 text-center">Ilość</td>
                                            <td className="px-4 py-3 text-right">Cena</td>
                                            <td className="px-4 py-3 text-right">Wartość</td>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {selectedOrder.items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="px-4 py-3">
                                                    <p className="font-bold text-gray-900">{item.title}</p>
                                                    {item.variantName && <p className="text-xs text-gray-500">{item.variantName}</p>}
                                                </td>
                                                <td className="px-4 py-3 text-center">{item.quantity}</td>
                                                <td className="px-4 py-3 text-right">{item.price} PLN</td>
                                                <td className="px-4 py-3 text-right font-bold">{(item.price * item.quantity).toFixed(2)} PLN</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50 font-bold border-t">
                                        <tr>
                                            <td colSpan={3} className="px-4 py-3 text-right">Dostawa:</td>
                                            <td className="px-4 py-3 text-right">{selectedOrder.delivery.cost.toFixed(2)} PLN</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={3} className="px-4 py-3 text-right text-base">RAZEM:</td>
                                            <td className="px-4 py-3 text-right text-base">{selectedOrder.totalAmount.toFixed(2)} PLN</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400 flex-col gap-4">
                    <ShoppingBag size={48} className="opacity-20"/>
                    <p>Wybierz zamówienie z listy, aby zobaczyć szczegóły.</p>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default OrderManager;
